import scores from './provider.scores.json'
import { get } from 'object-path'
import cloneDeep from 'lodash/cloneDeep'
// ----------------------------------------------------------------
// Computes the risk points due to risk factors and medication with
// qtc-prolonging drugs
// ----------------------------------------------------------------
// @rules array
/* @return
{
  "riskPoints": 12.75,
  // Risk factors based on answers
  "risks": [
    {
      "id": "years",
      "group": "age",
      "val": "senior"
    },
    {
      "id": "bio",
      "group": "gender",
      "val": "female"
    },
    {
      "id": "kalium",
      "group": "elektrolyt",
      "val": "low"
    },
    {
      "id": "omeprazol-adverse-2020-11-20-95371ff8",
      "group": "adverse",
      "drug": "omeprazol",
    },
    // Based on below
    "riskPointsUnknown": 1
    // Anwser unknwon has been selected
    "risksUnknwon": [
     {id: "crp", group: "entzündung", val: "unknown"}
  ]
*/
export function computeRisk( faqsById, rules, kineticRules ) {

  if ( Array.isArray( rules ) == false ) {
    rules = Object.values( rules )
  }

  const factors = Object.keys( scores )

  const risks = []
  var riskPoints = 0
  const risksUnknown = []
  var riskPointsUnknown = 0
  const risksPossible = []
  var riskPointsPossible = 0

  // Be sure to have not backreferences
  faqsById = cloneDeep( faqsById )
  // ------------------------------------
  // Compute the risk due to risk factors
  // ------------------------------------
  for ( const factor of factors ) {

    if ( typeof scores[ factor ] !== 'undefined' ) {
      const risk = computeRiskForFactor( faqsById, factor, scores )
      // Collect the results
      risks.push( ...risk.risk )
      riskPoints += risk.points
      risksUnknown.push( ...risk.riskUnknown )
      riskPointsUnknown += risk.pointsUnknown
      risksPossible.push( ...risk.riskPossible )
      riskPointsPossible += risk.pointsPossible
    }
  }

  const drugGroups = triageGroupIntoSubgroup( rules )

  // -------------------------------------------------------
  // Risk points due to medication with qtc-prolonging drugs
  // -------------------------------------------------------
  riskPoints += drugRiskPoints( drugGroups.known, 3, kineticRules )
  risks.push( ...drugGroups.known )
  riskPoints += drugRiskPoints( drugGroups.possible, 0.5, kineticRules )
  risks.push( ...drugGroups.possible )
  riskPoints += drugRiskPoints( drugGroups.conditional, 0.25, kineticRules )
  risks.push( ...drugGroups.conditional )

  return { riskPoints, risks, riskPointsUnknown, risksUnknown, riskPointsPossible, risksPossible }
}

// ---------------------------------------------
// Triages known, possible and conditional drugs
// ---------------------------------------------
function triageGroupIntoSubgroup( rules ) {
  return rules.reduce( ( acc, cur ) => {
    if ( cur.param.some( l => l.includes( "grad:possible" ) ) ) {
      acc.possible.push( cur )
    }
    if ( cur.param.some( l => l.includes( "grad:conditional" ) ) ) {
      acc.conditional.push( cur )
    }
    if ( cur.param.some( l => l.includes( "grad:known" ) ) ) {
      acc.known.push( cur )
    }
    return acc
  }, { possible: [], conditional: [], known: [] } )
}

// ----------------------------------------------------------------------------
//                              Helper functions
// ----------------------------------------------------------------------------
function computeRiskForFactor( faqsById, factor, scores ) {
  let points = 0
  const risk = []
  let pointsUnknown = 0
  const riskUnknown = []
  let pointsPossible = 0
  const riskPossible = []

  for ( const subfac of Object.keys( scores[ factor ] ) ) {
    if ( get( faqsById, `${factor}` ) === undefined ) {
      continue
    }
    // Pretend to be unknown
    if ( get( faqsById, `${factor}.${subfac}` ) == undefined ) {
      faqsById[ factor ][ subfac ] = "unknown"
      const score = riskScoresUnknown( faqsById, factor, subfac )
      var { newPoints, newRisk } = getRiskPointsForFactor( faqsById, factor, subfac, score, riskPossible, true )
      //console.log( "possible", factor, subfac, newPoints, score )
      pointsPossible += newPoints
      riskPossible.push( ...newRisk )
    }
    // Unknown risks
    else if ( get( faqsById, `${factor}.${subfac}` ) == "unknown" ) {
      const score = riskScoresUnknown( faqsById, factor, subfac )
      var { newPoints, newRisk } = getRiskPointsForFactor( faqsById, factor, subfac, score, riskUnknown, true )
      //console.log( "unknown", factor, subfac, newPoints, score )
      pointsUnknown += newPoints
      riskUnknown.push( ...newRisk )
    }
    // Actual risks
    else {
      const score = riskScores( faqsById, factor, subfac )
      var { newPoints, newRisk } = getRiskPointsForFactor( faqsById, factor, subfac, score, risk, false )
      //console.log( "real", factor, subfac, newPoints )
      points += newPoints
      risk.push( ...newRisk )
    }

  }
  return { points, risk, pointsUnknown, riskUnknown, pointsPossible, riskPossible }
}

function getRiskPointsForFactor( faqsById, factor, subfac, score, risk, unknown ) {
  let newPoints = 0
  const newRisk = []
  if ( score !== 0 ) {
    // Check the special case: "hypertension", "cardiomyopathy"
    // and "ejectionfraction" contribution is not cumulative, i.e.
    // We increase the score for only one (any) of them, but
    // we put all of them in the risks, in order to show in the texts.
    if ( factor !== 'heartdisease' || subfac === 'arrhythmia' ||
      risk.every( item => item.group !== 'heartdisease' ) ) {
      newPoints += score
    }
    if ( unknown && faqsById[ factor ][ subfac ] === 'unknown' ) {
      newRisk.push( {
        id: subfac,
        group: factor,
        val: faqsById[ factor ][ subfac ]
      } )
    } else if ( !unknown ) {
      newRisk.push( {
        id: subfac,
        group: factor,
        val: faqsById[ factor ][ subfac ]
      } )
    }
  }
  return { newPoints, newRisk }
}

export function riskScores( faqsById, type, subtype ) {
  var rule
  const val = faqsById[ type ][ subtype ]
  switch ( `${type}_${subtype}` ) {
    case 'ekg_qtc':
      const gen = faqsById.gender.bio
      rule = ( val === "mt450" && gen === "male" ) || ( val === "mt470" )
      break
    case 'age_years':
      rule = val === "senior" || val === "elderly"
      break
    case 'gender_bio':
      rule = val === "female"
      break
    case 'gewicht_bmi':
      rule = val === "obese"
      break
    case 'heartdisease_hypertension':
      rule = val === "yes" || val === "last5years"
      break
    case 'heartdisease_cardiomyopathy':
      rule = val === "yes" || val === "last5years"
      break
    case 'heartdisease_ejectionfraction':
      rule = val === "yes" || val === "last5years"
      break
    case 'heartdisease_arrhythmia':
      rule = val === "yes" || val === "last5years"
      break
    case 'metabolic_thyroid':
      rule = val === "yes"
      break
    case 'metabolic_diabetes':
      rule = val === "yes"
      break
    case 'liver_stage':
      rule = val === "failure"
      break
    case 'neurologic_general':
      rule = val === "yes" || val === "last5years"
      break
    case 'elektrolyt_kalium':
      rule = val === "low"
      break
    case 'elektrolyt_calcium':
      rule = val === "low"
      break
    case 'entzündung_crp':
      rule = val === "high"
      break
    case 'renal_gfr':
      rule = val === "15" || val === "30"
      break
    default:
      rule = false
  }
  return rule * scores[ type ][ subtype ].score
}

export function riskScoresUnknown( faqsById, type, subtype ) {
  const val = faqsById[ type ][ subtype ]
  const gen = faqsById.gender ? faqsById.gender.bio : null
  let rule
  if ( `${type}_${subtype}` === 'ekg_qtc' ) {
    if ( val === "mt450" && gen === 'unknown' ) {
      rule = true
    } else {
      rule = val === "unknown"
    }
  } else {
    rule = val === "unknown"
  }
  return rule * scores[ type ][ subtype ].score
}

const drugRiskPoints = function ( drugs, factor, kineticRules ) {
  return drugs.reduce( ( acc, cur ) => {
    const drugId = cur.drug
    let scale = 1
    if ( typeof kineticRules[ drugId ] != "undefined" ) {
      if ( kineticRules[ drugId ].predict && kineticRules[ drugId ].predict.net_effect == '+' ) {
        scale = 2
      } else if ( kineticRules[ drugId ].predict && kineticRules[ drugId ].predict.net_effect == '++' ) {
        scale = 3
      }
    }
    acc += factor * scale
    return acc
  }, 0 )
}